import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Shield, Activity, Zap, DollarSign, Clock, Target,
  TrendingUp, TrendingDown, CheckCircle, AlertTriangle, Download,
  Layers, Server, FlaskConical, GitBranch, FileText, Bug,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StatusDot = ({ status }) => {
  const colors = { elite: '#10b981', good: '#3b82f6', needs_improvement: '#f59e0b', poor: '#ef4444' };
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors[status] || '#6b7280', display: 'inline-block' }} />;
};

const KpiCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <div style={{
    padding: '1rem 1.125rem', background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
    borderRadius: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.375rem', minWidth: 0,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Icon size={15} style={{ color: 'hsl(var(--muted-foreground))', opacity: 0.6 }} />
      {trend === 'up' && <TrendingUp size={13} style={{ color: '#10b981' }} />}
      {trend === 'down' && <TrendingDown size={13} style={{ color: '#ef4444' }} />}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: color || 'inherit', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{value}</div>
    <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))', opacity: 0.7 }}>{sub}</div>}
  </div>
);

const DoraCard = ({ label, value, unit, target, status }) => (
  <div style={{
    padding: '1rem', background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
    borderRadius: '0.75rem', borderLeft: `3px solid ${status === 'elite' ? '#10b981' : status === 'good' ? '#3b82f6' : '#f59e0b'}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
      <StatusDot status={status} />
      <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', fontWeight: 700, color: status === 'elite' ? '#10b981' : status === 'good' ? '#3b82f6' : '#f59e0b', letterSpacing: '0.05em' }}>
        {status === 'elite' ? 'Elite' : status === 'good' ? 'Good' : 'Improve'}
      </span>
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>{unit}</div>
    <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.5rem' }}>{label}</div>
    <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>Target: {target} {unit}</div>
  </div>
);

const MiniBar = ({ value, max, color }) => (
  <div style={{ flex: 1, height: 6, background: 'hsl(var(--secondary))', borderRadius: 3, overflow: 'hidden' }}>
    <div style={{ width: `${Math.min(100, (value / max) * 100)}%`, height: '100%', background: color || 'hsl(var(--accent))', borderRadius: 3, transition: 'width 0.6s ease' }} />
  </div>
);

const SparkLine = ({ data, color, height }) => {
  if (!data?.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 120;
  const h = height || 32;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color || 'hsl(var(--accent))'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export default function ExecDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetch(`${API_URL}/api/executive/dashboard`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const downloadPDF = (type) => {
    window.open(`${API_URL}/api/reports/${type}`, '_blank');
  };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!data) return <div style={{ padding: '3rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Unable to load dashboard</div>;

  const sections = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'dora', label: 'DORA Metrics', icon: Activity },
    { key: 'testing', label: 'Testing KPIs', icon: FlaskConical },
    { key: 'devops', label: 'DevOps KPIs', icon: Server },
    { key: 'security', label: 'Security', icon: Shield },
    { key: 'cost', label: 'Cost', icon: DollarSign },
  ];

  return (
    <div data-testid="exec-dashboard-page" style={{ maxWidth: '1100px', margin: '0 auto', overflow: 'hidden' }}>
      <Link to="/history" style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.75rem' }}>
        <ArrowLeft size={14} /> Back
      </Link>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, letterSpacing: '-0.02em' }} data-testid="exec-dashboard-title">
            <BarChart3 size={22} style={{ display: 'inline', verticalAlign: '-3px', marginRight: '0.5rem', color: 'hsl(var(--accent))' }} />
            Executive Dashboard
          </h1>
          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>Engineering intelligence at a glance</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => downloadPDF('pitch-deck')} style={{ fontSize: '0.75rem' }} data-testid="download-pitch-deck">
            <Download size={13} style={{ marginRight: '0.25rem' }} /> Pitch Deck
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="tab-list" style={{ marginBottom: '1.25rem' }}>
        {sections.map(s => (
          <button key={s.key} className={`tab-trigger ${activeSection === s.key ? 'active' : ''}`}
            onClick={() => setActiveSection(s.key)} data-testid={`exec-tab-${s.key}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <s.icon size={14} /> {s.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeSection === 'overview' && (
        <div data-testid="exec-overview-panel">
          {/* Top KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <KpiCard icon={Layers} label="Total Projects" value={data.overview.total_projects} trend="up" />
            <KpiCard icon={GitBranch} label="Total Builds" value={data.overview.total_builds} />
            <KpiCard icon={CheckCircle} label="Success Rate" value={`${data.quality_metrics.build_success_rate}%`} color={data.quality_metrics.build_success_rate >= 80 ? '#10b981' : '#f59e0b'} trend="up" />
            <KpiCard icon={Clock} label="Avg Build Time" value={data.quality_metrics.avg_build_time} />
            <KpiCard icon={Shield} label="Security Score" value={`${data.quality_metrics.security_score_avg}%`} color="#10b981" />
          </div>

          {/* Quality Summary */}
          <div className="card" style={{ marginBottom: '1rem', padding: '1rem 1.25rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Engineering Quality Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {[
                { label: 'Build Success Rate', value: data.quality_metrics.build_success_rate, color: '#10b981' },
                { label: 'Test Coverage', value: data.quality_metrics.test_coverage_avg, color: '#3b82f6' },
                { label: 'Security Score', value: data.quality_metrics.security_score_avg, color: '#8b5cf6' },
                { label: 'Code Quality', value: data.quality_metrics.code_quality_avg, color: '#f59e0b' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', width: '130px', flexShrink: 0 }}>{item.label}</span>
                  <MiniBar value={item.value} max={100} color={item.color} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, width: '40px', textAlign: 'right' }}>{item.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trends */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
            <div className="card" style={{ padding: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>Builds (7-day trend)</div>
              <SparkLine data={data.trends.builds_last_7_days} color="#10b981" height={40} />
            </div>
            <div className="card" style={{ padding: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>Coverage (7-day trend)</div>
              <SparkLine data={data.trends.coverage_last_7_days} color="#3b82f6" height={40} />
            </div>
            <div className="card" style={{ padding: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>Security (7-day trend)</div>
              <SparkLine data={data.trends.security_last_7_days} color="#8b5cf6" height={40} />
            </div>
          </div>
        </div>
      )}

      {/* DORA METRICS */}
      {activeSection === 'dora' && (
        <div data-testid="exec-dora-panel">
          <div className="card" style={{ marginBottom: '1rem', padding: '1rem 1.25rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>DORA Metrics</h3>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              Industry-standard DevOps Research and Assessment metrics measuring software delivery performance
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {Object.entries(data.dora_metrics).map(([key, m]) => (
                <DoraCard key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={m.value} unit={m.unit} target={m.target} status={m.status} />
              ))}
            </div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>DORA Performance Benchmarks</h4>
            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><StatusDot status="elite" /> <strong>Elite:</strong> On-demand deployment, &lt;1hr lead time, 0-15% failure rate, &lt;1hr recovery</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><StatusDot status="good" /> <strong>High:</strong> Daily-weekly deployment, 1d-1wk lead time, 16-30% failure rate, &lt;1d recovery</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><StatusDot status="needs_improvement" /> <strong>Medium:</strong> Monthly deployment, 1wk-1mo lead time, &gt;30% failure rate, &gt;1d recovery</div>
            </div>
          </div>
        </div>
      )}

      {/* TESTING KPIs */}
      {activeSection === 'testing' && (
        <div data-testid="exec-testing-panel">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <KpiCard icon={FlaskConical} label="Test Suites" value={data.testing_kpis.total_test_suites} />
            <KpiCard icon={BarChart3} label="Avg Coverage" value={`${data.testing_kpis.avg_coverage}%`} color={data.testing_kpis.avg_coverage >= 70 ? '#10b981' : '#f59e0b'} />
            <KpiCard icon={CheckCircle} label="Pass Rate" value={`${data.testing_kpis.pass_rate}%`} color={data.testing_kpis.pass_rate >= 90 ? '#10b981' : '#f59e0b'} />
            <KpiCard icon={Bug} label="Flaky Rate" value={`${data.testing_kpis.flaky_test_rate}%`} color={data.testing_kpis.flaky_test_rate < 5 ? '#10b981' : '#ef4444'} />
            <KpiCard icon={Clock} label="Avg Execution" value={data.testing_kpis.avg_execution_time} />
            <KpiCard icon={Target} label="Defect Escape" value={`${data.testing_kpis.defect_escape_rate}%`} color={data.testing_kpis.defect_escape_rate < 5 ? '#10b981' : '#ef4444'} sub="Lower is better" />
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Testing Quality Breakdown</h3>
            {[
              { label: 'Test Automation Ratio', value: data.testing_kpis.test_automation_ratio, target: 90, color: '#10b981' },
              { label: 'Regression Catch Rate', value: data.testing_kpis.regression_catch_rate, target: 95, color: '#3b82f6' },
              { label: 'Average Coverage', value: data.testing_kpis.avg_coverage, target: 80, color: '#8b5cf6' },
              { label: 'Pass Rate', value: data.testing_kpis.pass_rate, target: 95, color: '#f59e0b' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
                <span style={{ fontSize: '0.75rem', width: '160px', flexShrink: 0 }}>{item.label}</span>
                <MiniBar value={item.value} max={100} color={item.color} />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, width: '40px', textAlign: 'right' }}>{item.value}%</span>
                <span style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))', width: '60px' }}>/ {item.target}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DEVOPS KPIs */}
      {activeSection === 'devops' && (
        <div data-testid="exec-devops-panel">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <KpiCard icon={Activity} label="Pipeline Success" value={`${data.devops_kpis.pipeline_success_rate}%`} color={data.devops_kpis.pipeline_success_rate >= 90 ? '#10b981' : '#f59e0b'} />
            <KpiCard icon={Clock} label="Avg Deploy Time" value={data.devops_kpis.avg_deploy_time} />
            <KpiCard icon={DollarSign} label="Infra Cost/mo" value={`$${data.devops_kpis.infra_cost_monthly}`} />
            <KpiCard icon={Server} label="Container Health" value={`${data.devops_kpis.container_health}%`} color="#10b981" />
            <KpiCard icon={Target} label="Uptime SLA" value={`${data.devops_kpis.uptime_sla}%`} color="#10b981" />
            <KpiCard icon={AlertTriangle} label="Incidents (30d)" value={data.devops_kpis.incident_count_30d} color={data.devops_kpis.incident_count_30d <= 2 ? '#10b981' : '#ef4444'} />
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>DevOps Health Indicators</h3>
            {[
              { label: 'Pipeline Success Rate', value: data.devops_kpis.pipeline_success_rate, color: '#10b981' },
              { label: 'Container Health', value: data.devops_kpis.container_health, color: '#3b82f6' },
              { label: 'Compliance Score', value: data.devops_kpis.compliance_score, color: '#8b5cf6' },
              { label: 'Uptime SLA', value: data.devops_kpis.uptime_sla, color: '#f59e0b' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
                <span style={{ fontSize: '0.75rem', width: '160px', flexShrink: 0 }}>{item.label}</span>
                <MiniBar value={item.value} max={100} color={item.color} />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, width: '40px', textAlign: 'right' }}>{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECURITY */}
      {activeSection === 'security' && (
        <div data-testid="exec-security-panel">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            <div className="card" style={{ flex: '1 1 200px', padding: '1.25rem', textAlign: 'center' }}>
              <div style={{
                width: 100, height: 100, borderRadius: '50%', margin: '0 auto 0.75rem',
                border: `5px solid ${data.security_posture.overall_score >= 80 ? '#10b981' : '#f59e0b'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: data.security_posture.overall_score >= 80 ? '#10b981' : '#f59e0b' }}>
                  {data.security_posture.overall_score}
                </div>
              </div>
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>Security Posture Score</div>
              <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Overall risk assessment</div>
            </div>
            <div style={{ flex: '2 1 300px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
              <KpiCard icon={AlertTriangle} label="Critical Vulns" value={data.security_posture.critical_vulns} color={data.security_posture.critical_vulns === 0 ? '#10b981' : '#ef4444'} />
              <KpiCard icon={AlertTriangle} label="High Vulns" value={data.security_posture.high_vulns} color={data.security_posture.high_vulns <= 3 ? '#f59e0b' : '#ef4444'} />
              <KpiCard icon={Shield} label="OWASP Compliance" value={`${data.security_posture.owasp_compliance}%`} color="#10b981" />
              <KpiCard icon={Shield} label="Secret Scan Pass" value={`${data.security_posture.secret_scan_pass_rate}%`} color="#10b981" />
              <KpiCard icon={Layers} label="Dependency Health" value={`${data.security_posture.dependency_health}%`} color="#3b82f6" />
              <KpiCard icon={Shield} label="Medium Vulns" value={data.security_posture.medium_vulns} color="#f59e0b" />
            </div>
          </div>
        </div>
      )}

      {/* COST INTELLIGENCE */}
      {activeSection === 'cost' && (
        <div data-testid="exec-cost-panel">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <KpiCard icon={DollarSign} label="Monthly Total" value={`$${data.cost_intelligence.total_monthly_cost}`} color="#10b981" sub="Infrastructure + AI" />
            <KpiCard icon={Zap} label="Cost per Build" value={`$${data.cost_intelligence.cost_per_build}`} sub="Average across builds" />
            <KpiCard icon={Activity} label="Token Cost/mo" value={`$${data.cost_intelligence.token_cost_monthly}`} sub="LLM API usage" />
            <KpiCard icon={Server} label="Infra Cost/mo" value={`$${data.cost_intelligence.infra_cost_monthly}`} sub="Compute + storage" />
            <KpiCard icon={TrendingDown} label="Savings Opportunity" value={`$${data.cost_intelligence.optimization_savings}`} color="#10b981" sub="Estimated monthly savings" />
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Cost Breakdown</h3>
            {[
              { label: 'AI/LLM Token Costs', value: data.cost_intelligence.token_cost_monthly, max: data.cost_intelligence.total_monthly_cost, color: '#8b5cf6' },
              { label: 'Infrastructure', value: data.cost_intelligence.infra_cost_monthly, max: data.cost_intelligence.total_monthly_cost, color: '#3b82f6' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
                <span style={{ fontSize: '0.75rem', width: '140px', flexShrink: 0 }}>{item.label}</span>
                <MiniBar value={item.value} max={item.max} color={item.color} />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, width: '50px', textAlign: 'right' }}>${item.value}</span>
              </div>
            ))}
            <div style={{ marginTop: '0.75rem', padding: '0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', fontSize: '0.75rem' }}>
              <span style={{ fontWeight: 600 }}>Trend: </span>
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>{data.cost_intelligence.cost_trend === 'stable' ? 'Costs are stable month-over-month' : data.cost_intelligence.cost_trend === 'decreasing' ? 'Costs trending downward' : 'Costs increasing - review optimization suggestions'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
